'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

import classNames from 'classnames';
import { configResponsive, useResponsive } from 'ahooks';

// 全局配置断点
configResponsive({
  sm: 480,
  md: 768,
  lg: 1050,
  xl: 1546,
});

type SidebarContext = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  handleMobileClose: () => void;
  toggleSidebar: () => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
};

const SidebarContext = createContext<SidebarContext | null>(null);

const SidebarProvider = ({ children }: { children: React.ReactNode }) => {
  const responsive = useResponsive();
  const [open, setOpen] = React.useState(responsive.lg);
  // cont
  const toggleSidebar = () => {
    setOpen((open) => !open);
  };

  const handleMobileClose = () => {
    if (!responsive.lg) {
      setOpen(false);
    }
  };

  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchMoveX, setTouchMoveX] = useState<number | null>(null);
  const sidebarWidth = 256; // 假设侧边栏宽度为256px，可以替换为实际的--left-model-width值

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX !== null) {
      setTouchMoveX(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    if (touchStartX !== null && touchMoveX !== null) {
      const deltaX = touchMoveX - touchStartX;
      const thresholdDistance = sidebarWidth * 0.5; // 50%的阈值

      // 向右滑动超过阈值，打开侧边栏
      if (deltaX > thresholdDistance && !open) {
        setOpen(true);
      }
      // 向左滑动超过阈值，关闭侧边栏
      else if (deltaX < -thresholdDistance && open) {
        setOpen(false);
      }
    }

    // 重置触摸状态
    setTouchStartX(null);
    setTouchMoveX(null);
  };

  const state = open ? 'expanded' : 'collapsed';
  const contextValue = React.useMemo<SidebarContext>(
    () => ({
      state,
      open,
      setOpen,
      toggleSidebar,
      handleMobileClose,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    }),
    [
      state,
      open,
      setOpen,
      toggleSidebar,
      handleMobileClose,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
    ],
  );

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};

function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    console.error('useSidebar must be used within a SidebarProvider');
    return {
      state: 'collapsed',
      open: false,
      setOpen: () => {
        console.log('Sidebar 不支持 setOpen');
      },
      handleMobileClose: () => {
        console.log('Sidebar 不支持 handleMobileClose');
      },
      toggleSidebar: () => {
        console.log('Sidebar 不支持 toggleSidebar');
      },
      handleTouchStart: () => {
        console.log('Sidebar 不支持 handleTouchStart');
      },
      handleTouchMove: () => {
        console.log('Sidebar 不支持 handleTouchMove');
      },
      handleTouchEnd: () => {
        console.log('Sidebar 不支持 handleTouchEnd');
      },
    };
  }
  return context;
}

function Sidebar({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const { setOpen, open, handleTouchStart, handleTouchMove, handleTouchEnd } = useSidebar();
  const responsive = useResponsive();
  const [openHoverSidebar, setOpenHoverSidebar] = useState(false);

  // 监听屏幕宽度变化
  useEffect(() => {
    setOpen(responsive.lg);
  }, [responsive.lg]);

  useEffect(() => {
    if (open) {
      setOpenHoverSidebar(false);
    }
  }, [open]);

  const navContainerClass = classNames(
    'relative h-[var(--screen)] transition-[width] duration-300 z-[120] bg-col_bg09 md:bg-col_bg12 overflow-hidden flex flex-col',
    open ? 'w-[var(--left-model-width)]' : 'w-[0]',
  );

  const hoverNavContainerClass = classNames(
    'fixed  top-[var(--header-height)] py-4 transition-all  border border-col_line00 rounded-[16px] shadow-s1 duration-300   z-[120] bg-col_bg09 md:bg-col_bg12 overflow-hidden flex flex-col',
    openHoverSidebar ? 'w-[var(--left-model-width)] left-4' : 'w-[0] left-[-10px]',
    open ? 'hidden' : '',
  );

  // 鼠标移出事件处理函数
  const handleMouseLeave = () => {
    if (!responsive.lg) {
      setOpen(false);
    }
  };
  const closeSidebar = () => {
    setOpen(false);
  };
  //手指滑动控制侧边栏的展示或者隐藏

  return (
    <aside
      className='bottom-0 left-0 top-0 z-[120] h-full md:fixed lg:static '
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={navContainerClass} onMouseLeave={handleMouseLeave} style={style}>
        {children}
      </div>
      {!open && (
        <div
          className='fixed left-0 top-[var(--header-height)] hidden min-h-full w-6 md:block '
          onMouseEnter={() => setOpenHoverSidebar(true)}
          onMouseLeave={() => {
            setOpenHoverSidebar(false);
          }}
        >
          <div className={hoverNavContainerClass}>
            <div className='w-[var(--left-model-width)]'>{children}</div>
          </div>
        </div>
      )}
      {open && (
        <div
          className='fixed bottom-0 left-0 right-0 top-0 z-[100] bg-black/[0.07] md:hidden'
          onClick={closeSidebar}
        ></div>
      )}
    </aside>
  );
}

export { useSidebar, Sidebar, SidebarProvider };
